import {
    PriceQuantity,
    SelectedArticle,
    SetArticleQuantityCommand,
    SetPriceQuantityCommand,
} from '@/containers/widget/useWidgetPreview';

export type KnownQuantities = Record<number, number>;

export interface QuantityRow {
    id: number;
    quantity: number;
}

export interface SyncPlan {
    known: KnownQuantities;
    changes: QuantityRow[];
}

export interface ArticleSyncPlan {
    known: KnownQuantities;
    commands: SetArticleQuantityCommand[];
}

export interface PriceSyncPlan {
    known: KnownQuantities;
    commands: SetPriceQuantityCommand[];
}

export const planSyncRows = (
    known: KnownQuantities,
    rows: QuantityRow[],
    syncedIds: number[]
): SyncPlan => {
    const next: KnownQuantities = Object.fromEntries(
        syncedIds.map((id) => [id, quantityOf(rows, id)])
    );
    const changedId = syncedIds.find((id) => next[id] !== (known[id] ?? 0));
    if (changedId === undefined) return { known: next, changes: [] };
    const quantity = next[changedId];
    const changes = syncedIds
        .filter((id) => id !== changedId && next[id] !== quantity)
        .map((id) => ({ id, quantity }));
    return { known: next, changes };
};

export const planSyncCommands = (
    known: KnownQuantities,
    articles: SelectedArticle[],
    syncedArticleIds: number[]
): ArticleSyncPlan => {
    const plan = planSyncRows(known, articles.map(toArticleRow), syncedArticleIds);
    return {
        known: plan.known,
        commands: plan.changes.map(({ id, quantity }) => ({ articleId: id, quantity })),
    };
};

export const planPriceSyncCommands = (
    known: KnownQuantities,
    quantities: PriceQuantity[],
    syncedPriceIds: number[]
): PriceSyncPlan => {
    const plan = planSyncRows(known, quantities.map(toPriceRow), syncedPriceIds);
    return {
        known: plan.known,
        commands: plan.changes.map(({ id, quantity }) => ({ priceId: id, quantity })),
    };
};

const toArticleRow = (article: SelectedArticle): QuantityRow => ({
    id: article.ArticleId,
    quantity: article.Quantity,
});

const toPriceRow = (price: PriceQuantity): QuantityRow => ({
    id: price.PriceId,
    quantity: price.Quantity,
});

const quantityOf = (rows: QuantityRow[], id: number): number =>
    rows.find((row) => row.id === id)?.quantity ?? 0;

export const toggleId = (ids: number[], id: number): number[] =>
    ids.includes(id) ? ids.filter((current) => current !== id) : [...ids, id];

export const toggleArticleId = toggleId;
